// src/pages/inventory/ProductImportPreview.jsx
import { useState, useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import ThemeMaker from "../../components/thememaker";
import LoaderSpiner from "../../components/LoaderSpiner/LoaderSpiner";
import { httpService } from "../../services/httpService";
import DynamicTable from "../../components/common/DynamicTable";
import { FaCheck, FaTimes, FaExclamationTriangle } from "react-icons/fa";
import { HiArrowSmLeft } from "react-icons/hi";

const ProductImportPreview = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { excelData, fileName } = location.state || {};
  
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [importStats, setImportStats] = useState({
    total: 0,
    valid: 0,
    invalid: 0,
  });

  // ✅ Extract the actual data array from the response
  const tableData = useMemo(() => {
    // If excelData is already an array, use it
    if (Array.isArray(excelData)) {
      return excelData;
    }
    
    // If excelData has validation_results, combine success and warnings
    if (excelData?.validation_results) {
      const success = excelData.validation_results.success || [];
      const warnings = excelData.validation_results.warnings || [];
      const errors = excelData.validation_results.errors || [];
      
      // Combine all rows with their status
      const allRows = [
        ...success.map(row => ({ ...row, isValid: true })),
        ...warnings.map(row => ({ ...row, isValid: true, warnings: row.warnings || [] })),
        ...errors.map(row => ({ ...row, isValid: false, errors: row.issues || [] })),
      ];
      
      return allRows;
    }
    
    // If excelData has a data property
    if (excelData?.data && Array.isArray(excelData.data)) {
      return excelData.data;
    }
    
    // Fallback: return empty array
    return [];
  }, [excelData]);

  // ✅ Get import history ID
  const importHistoryId = useMemo(() => {
    return excelData?.importHistoryId || null;
  }, [excelData]);

  useEffect(() => {
    if (!excelData) {
      toast.error("No data found. Please try again.");
      navigate("/admin/inventory-management/product-import");
      return;
    }

    // Calculate stats from the extracted data
    const total = tableData.length;
    const valid = tableData.filter(row => row.isValid !== false).length;
    const invalid = total - valid;
    setImportStats({ total, valid, invalid });
    
    // Select all valid rows by default
    const validIds = tableData
      .filter(row => row.isValid !== false)
      .map((_, index) => index);
    setSelectedRows(validIds);
  }, [tableData, excelData, navigate]);

  const handleImport = async () => {
    if (selectedRows.length === 0) {
      toast.warning("Please select at least one row to import.", {
        position: "top-center",
      });
      return;
    }

    // Separate success and warning rows
    const successData = selectedRows
    .filter((index) => tableData[index]?.isValid !== false && 
                        !tableData[index]?.warnings?.length)
    .map((index) => tableData[index]);

    const warningData = selectedRows
    .filter((index) => tableData[index]?.isValid !== false && 
                        tableData[index]?.warnings?.length > 0)
    .map((index) => tableData[index]);

    const errorData = tableData.filter((row) => row.isValid === false);

    const totalSelected = selectedRows.length;

    try {
      setImporting(true);
      const response = await httpService.post(
        "/vendorproduct/saveImportVendorProducts",
        {
          importTag: "excel",
          importDetails: {
            importHistoryId: importHistoryId,
            excelData: {
              success: successData,
              warnings: warningData,
              errors: errorData,
            },
          },
        },
      );

      if (response.status) {
        // ✅ FIX: Use the backend message directly
        toast.success(response.message, {
            position: "top-center",
        });
        navigate("/admin/inventory-management/products");
      } else {
        toast.error(
          response.data.message || "Import failed. Please try again.",
          {
            position: "top-center",
          },
        );
      }
    } catch (error) {
      toast.error("Error importing products: " + error.message, {
        position: "top-center",
      });
    } finally {
      setImporting(false);
    }
  };

  const handleRowSelection = (index) => {
    setSelectedRows(prev => {
      if (prev.includes(index)) {
        return prev.filter(i => i !== index);
      } else {
        return [...prev, index];
      }
    });
  };

  const getStatusIcon = (row) => {
    if (row.isValid === false) {
      return <FaTimes className="text-red-500" />;
    }
    if (row.isValid === true) {
      return <FaCheck className="text-green-500" />;
    }
    return <FaExclamationTriangle className="text-yellow-500" />;
  };

  const columns = [
    {
    key: "row_number",
    header: "#",
    sortable: false,
    renderCell: (row) => {
        const idx = tableData.indexOf(row);
        return (
        <div className="flex items-center gap-2">
            <input
            type="checkbox"
            checked={selectedRows.includes(idx)}
            onChange={() => handleRowSelection(idx)}
            disabled={row.isValid === false}
            className="w-4 h-4 text-blue-600 rounded"
            />
            <span className="text-gray-500">{row.row_number ?? idx + 1}</span>
        </div>
        );
    },
    },
    {
      key: "product_name",
      header: "Product Name",
      sortable: true,
      renderCell: (row) => {
        // Try to get product name from different possible locations
        const name = row.original_data?.name || row.product_name || row.name || "—";
        const sku = row.original_data?.sku_number || row.sku_number || row.sku || "—";
        return (
          <div>
            <div className="font-medium">{name}</div>
            <div className="text-xs text-gray-500">SKU: {sku}</div>
          </div>
        );
      },
    },
    {
      key: "category",
      header: "Category",
      sortable: true,
      renderCell: (row) => {
        const category = row.original_data?.product_category || row.category_name || row.category || "—";
        return <span>{category}</span>;
      },
    },
    {
      key: "prices",
      header: "Prices",
      sortable: true,
      renderCell: (row) => {
        const mrp = row.original_data?.mrp || row.mrp || 0;
        const purchase = row.original_data?.purchase_price || row.purchase_price || 0;
        const sale = row.original_data?.sale_price || row.sale_price || 0;
        return (
          <div className="text-sm">
            <div>MRP: ₹{mrp}</div>
            <div>Purchase: ₹{purchase}</div>
            <div>Sale: ₹{sale}</div>
          </div>
        );
      },
    },
    {
      key: "stock",
      header: "Stock",
      sortable: true,
      renderCell: (row) => {
        const stock = row.original_data?.qty || row.original_data?.['Opening Stock'] || row.opening_stock || row.qty || 0;
        return <span>{stock}</span>;
      },
    },
    {
      key: "medicine_info",
      header: "Medicine Info",
      sortable: false,
      renderCell: (row) => {
        const isMedicine = row.original_data?.is_medicine || row.is_medicine || false;
        if (isMedicine === 'Yes' || isMedicine === true) {
          const batch = row.original_data?.batch_number || row.batch_number || "—";
          const expiry = row.original_data?.expiry_date || row.expiry_date || "—";
          return (
            <div className="text-sm">
              <div>Batch: {batch}</div>
              <div>Expiry: {expiry}</div>
            </div>
          );
        }
        return <span className="text-gray-400">—</span>;
      },
    },
    {
    key: "status",
    header: "Status",
    sortable: true,
    renderCell: (row) => (
        <div className="flex items-center gap-1">
        {getStatusIcon(row)}
        <span className={`text-sm ${row.isValid === false ? 'text-red-600' : 'text-green-600'}`}>
            {row.isValid === false ? 'Invalid' : 'Valid'}
        </span>
        {row.errors && row.errors.length > 0 && (
            <div className="relative group">
            <FaExclamationTriangle className="text-yellow-500 cursor-help" />
            <div className="absolute bottom-full left-0 mb-1 hidden group-hover:block bg-gray-800 text-white text-xs rounded p-2 w-48 z-50">
                {row.errors.map((err, i) => (
                // ✅ Fix: Extract the message from the error object
                <div key={i}>• {err.message || err}</div>
                ))}
            </div>
            </div>
        )}
        </div>
    ),
    },
  ];

  return (
    <ThemeMaker>
      {loading || importing ? <LoaderSpiner text={importing ? "Importing products..." : "Loading..."} /> : null}
      
      <div className="p-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate("/admin/inventory-management/product-import")}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <HiArrowSmLeft className="w-6 h-6" />
            </button>
            <div>
              <h1 className="text-2xl font-semibold">Preview Import</h1>
              <p className="text-sm text-gray-500">
                File: {fileName || "Unknown"} • {tableData.length || 0} rows
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <div className="flex gap-3 text-sm">
              <div className="bg-green-50 px-3 py-1 rounded-full">
                <span className="text-green-600 font-semibold">{importStats.valid}</span>
                <span className="text-gray-500 ml-1">Valid</span>
              </div>
              <div className="bg-red-50 px-3 py-1 rounded-full">
                <span className="text-red-600 font-semibold">{importStats.invalid}</span>
                <span className="text-gray-500 ml-1">Invalid</span>
              </div>
              <div className="bg-blue-50 px-3 py-1 rounded-full">
                <span className="text-blue-600 font-semibold">{selectedRows.length}</span>
                <span className="text-gray-500 ml-1">Selected</span>
              </div>
            </div>
            
            <button
              onClick={handleImport}
              disabled={selectedRows.length === 0 || importing}
              className={`px-6 py-2 rounded-md text-white font-medium transition-colors ${
                selectedRows.length === 0 || importing
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-blue-600 hover:bg-blue-700"
              }`}
            >
              {importing ? "Importing..." : `Import ${selectedRows.length} Products`}
            </button>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <DynamicTable
            columns={columns}
            data={tableData}
            searchable={true}
            emptyMessage="No data to preview"
          />
        </div>
      </div>
    </ThemeMaker>
  );
};

export default ProductImportPreview;